import React from "react";

/**
 * Animated SVG visualization of the protocol's chained stimuli.
 * Five nodes connected by a flowing wave path, each pulsing in sequence.
 */
export default function StimuliChain({ className = "", labels }) {
  const defaultLabels = ["Mécanique", "Électromagnétique", "Radiale", "Lumineux", "Énergétique"];
  const items = labels || defaultLabels;

  return (
    <div className={`relative w-full h-full ${className}`} data-testid="stimuli-chain">
      <svg
        viewBox="0 0 600 460"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Chain of stimuli visualization"
      >
        <defs>
          <linearGradient id="goldLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#8F6E1E" stopOpacity="0" />
            <stop offset="25%" stopColor="#8F6E1E" stopOpacity="0.9" />
            <stop offset="75%" stopColor="#8F6E1E" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#8F6E1E" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
            <stop offset="60%" stopColor="#A8852C" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#A8852C" stopOpacity="0" />
          </radialGradient>
          <pattern id="dots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#D9D2BF" />
          </pattern>
        </defs>

        {/* Background dot pattern */}
        <rect width="600" height="460" fill="url(#dots)" opacity="0.55" />

        {/* Outer frame */}
        <rect x="20" y="20" width="560" height="420" fill="none" stroke="#D9D2BF" strokeWidth="1" />

        {/* Center caption */}
        <text x="300" y="60" textAnchor="middle" fontFamily="Outfit, sans-serif" fontSize="11" letterSpacing="3.5" fill="#8F6E1E" fontWeight="600">
          ENCHAÎNEMENT · STIMULI
        </text>

        {/* Flowing wave path connecting nodes */}
        <path
          d="M 75 230 Q 150 130 225 230 T 375 230 T 525 230"
          stroke="url(#goldLine)"
          strokeWidth="1.6"
          fill="none"
        >
          <animate attributeName="stroke-dasharray" from="0,800" to="800,0" dur="3.6s" repeatCount="indefinite" />
        </path>

        {/* Secondary subtle wave below */}
        <path
          d="M 75 270 Q 150 350 225 270 T 375 270 T 525 270"
          stroke="#0F2540"
          strokeOpacity="0.18"
          strokeWidth="1.2"
          fill="none"
        />

        {/* Numbered phase ticks */}
        {items.map((label, i) => {
          const positions = [75, 187.5, 300, 412.5, 525];
          const cx = positions[i];
          const num = String(i + 1).padStart(2, "0");
          return (
            <g key={i}>
              {/* Outer glow */}
              <circle cx={cx} cy="230" r="34" fill="url(#nodeGlow)">
                <animate attributeName="opacity" values="0.45;1;0.45" dur="2.4s" begin={`${i * 0.45}s`} repeatCount="indefinite" />
              </circle>
              {/* Core */}
              <circle cx={cx} cy="230" r="11" fill="#0E0E12" stroke="#8F6E1E" strokeWidth="1.4" />
              <circle cx={cx} cy="230" r="3" fill="#D4AF37" />
              {/* Label above */}
              <text x={cx} y="186" textAnchor="middle" fontFamily="Outfit, sans-serif" fontSize="10" letterSpacing="2" fill="#0E0E12" fontWeight="500">
                {num}
              </text>
              {/* Label below */}
              <text x={cx} y="286" textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="11" fill="#4A4A52">
                {label}
              </text>
            </g>
          );
        })}

        {/* Bottom legend */}
        <line x1="40" y1="370" x2="560" y2="370" stroke="#D9D2BF" strokeWidth="1" />
        <text x="40" y="395" fontFamily="Outfit, sans-serif" fontSize="10" letterSpacing="3" fill="#8F6E1E" fontWeight="600">
          ORDRE · COHÉRENCE · CUMUL
        </text>
        <text x="560" y="395" textAnchor="end" fontFamily="Manrope, sans-serif" fontSize="11" fill="#4A4A52" fontStyle="italic">
          Riviera Protocol
        </text>

        {/* Corner ticks */}
        <g stroke="#8F6E1E" strokeWidth="1.5">
          <line x1="20" y1="20" x2="34" y2="20" />
          <line x1="20" y1="20" x2="20" y2="34" />
          <line x1="580" y1="20" x2="566" y2="20" />
          <line x1="580" y1="20" x2="580" y2="34" />
          <line x1="20" y1="440" x2="34" y2="440" />
          <line x1="20" y1="440" x2="20" y2="426" />
          <line x1="580" y1="440" x2="566" y2="440" />
          <line x1="580" y1="440" x2="580" y2="426" />
        </g>
      </svg>
    </div>
  );
}
